import React, { forwardRef } from 'react';
import styled from 'styled-components';

import useHover from 'shared/hooks/use-hover';
import { DragImage } from 'src/dispensary-admin/customize/homepage/sections/components/drag-image';
import { RemoveButton } from 'src/dispensary-admin/customize/homepage/sections/components/remove-button';

import { GqlCustomNavigationItem } from 'types/graphql';
import { getItemTypeLabel } from '../custom-navigation.constants';

type NavigationItemContentProps = {
  isDragging?: boolean;
  isSection?: boolean;
  item: GqlCustomNavigationItem;
  onEdit: () => void;
  onItemClick?: () => void;
  onRemove: () => void;
};

export const NavigationItemContent = forwardRef<HTMLDivElement, NavigationItemContentProps>(
  ({ item, isDragging = false, isSection = false, onRemove, onEdit, onItemClick }, ref) => {
    const [hoverRef, isHovered] = useHover();

    return (
      <Container
        ref={ref}
        $isDragging={isDragging}
        $isSection={isSection}
        onClick={isSection ? onItemClick : undefined}
      >
        <HoverContainer ref={hoverRef}>
          <ItemInfo>
            <ItemLabel>
              {item.label}
              <ItemType>{getItemTypeLabel(item.navigationType)}</ItemType>
              {isSection && <ItemCount>{item.items.length} items</ItemCount>}
            </ItemLabel>
            <ItemValue>{item.url && <ItemUrl>{item.url}</ItemUrl>}</ItemValue>
          </ItemInfo>
          <ActionsContainer>
            <EditButton
              $visible={isHovered && !isDragging}
              onClick={(e) => {
                e.stopPropagation();
                onEdit();
              }}
            >
              Edit
            </EditButton>
            <RemoveButton
              visible={isHovered && !isDragging}
              onClick={(e: React.MouseEvent) => {
                e.stopPropagation();
                onRemove();
              }}
            />
            <DragImage disabled={false} />
            {isSection && <ChevronIcon>›</ChevronIcon>}
          </ActionsContainer>
        </HoverContainer>
      </Container>
    );
  }
);

NavigationItemContent.displayName = 'NavigationItemContent';

const Container = styled.div<{ $isDragging: boolean; $isSection: boolean }>`
  align-items: center;
  background: ${({ $isDragging }) => ($isDragging ? '#F3F6F8' : '#FFFFFF')};
  border: 1px solid #d3d8de;
  border-radius: 6px;
  box-shadow: 0px 3px 5px #e9ecf1;
  cursor: ${({ $isSection }) => ($isSection ? 'pointer' : 'default')};
  display: flex;
  min-height: 64px;
  margin-bottom: 8px;
  opacity: ${({ $isDragging }) => ($isDragging ? 0 : 1)};
  padding: 16px 18px;
  width: 100%;

  &:hover {
    background: #f9fafb;
  }
`;

const HoverContainer = styled.div`
  align-items: center;
  display: flex;
  height: 100%;
  justify-content: space-between;
  width: 100%;
`;

const ItemInfo = styled.div`
  flex: 1 1 auto;
  margin-right: 12px;
  min-width: 0;
`;

const ItemLabel = styled.span`
  align-items: flex-end;
  display: flex;
  font-size: 14px;
  font-weight: bold;
  gap: 8px;
  line-height: 0.8;
  margin-bottom: 4px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const ItemValue = styled.div`
  align-items: center;
  display: flex;
  font-size: 12px;
  gap: 4px;
  line-height: 1;
`;

const ItemType = styled.span`
  color: #969ea5;
  display: inline-block;
  font-size: 10px;
  text-transform: uppercase;
`;

const ItemUrl = styled.span`
  display: inline-block;
  max-width: 200px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const ActionsContainer = styled.div`
  align-items: center;
  display: flex;
  gap: 8px;
`;

const EditButton = styled.button<{ $visible: boolean }>`
  appearance: none;
  background-color: transparent;
  border: none;
  color: ${({ theme }) => theme.colors.primaryBlue};
  cursor: pointer;
  font-size: 13px;
  line-height: 1;
  opacity: ${({ $visible }) => ($visible ? 1 : 0)};
  padding: 4px 8px;
  transition: opacity 0.15s;

  &:hover {
    text-decoration: underline;
  }
`;

const ChevronIcon = styled.span`
  color: #969ea5;
  font-size: 24px;
  font-weight: bold;
  line-height: 1;
`;

const ItemCount = styled.span`
  color: #677882;
  font-size: 11px;
  font-weight: normal;
`;
